import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import ArticleCard from "@/components/ArticleCard";
import { seriesFromSlug } from "@/lib/series";

export default function Series() {
  const { name: slug } = useParams();
  const name = seriesFromSlug(slug);
  const LOCKUPS = {
    "after-the-whistle": "after-the-whistle-lockup-on-light.svg",
    "the-mental-rep": "mental-rep-lockup-on-light.svg",
    "time-out": "time-out-lockup-on-light.png",
    "penalty": "penalty-lockup-on-light.png",
    "instant-replay": "instant-replay-lockup-on-light.png",
    "micd-up": "micd-up-lockup-on-light.png",
  };
  const lockup = LOCKUPS[slug];
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!name) { setLoading(false); return; }
    setLoading(true);
    (async () => {
      const { data } = await api.get(`/articles?series=${encodeURIComponent(name)}`);
      setArticles(data || []);
      setLoading(false);
    })();
  }, [name]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? articles.filter((a) => (a.title || "").toLowerCase().includes(q) || (a.excerpt || "").toLowerCase().includes(q))
    : articles;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20" data-testid="series-page">
      <Seo title={name ? `${name} — Series` : "Series"} description={`${name || "Series"} — the ${name || ""} series on VoluntoldHQ.`} path={`/series/${slug}`} />
      <div className="border-b-2 border-[color:var(--vt-dark)] pb-6 mb-10">
        <span className="vt-eyebrow vt-eyebrow--cream">Series</span>
        {lockup ? (
          <>
            <img src={`/brand/logos/${lockup}`} alt={name} className="mt-2 w-full max-w-[520px] h-auto" />
            <h1 className="sr-only">{name}</h1>
          </>
        ) : (
          <h1 className="mt-2 font-head text-[color:var(--vt-body-on-cream)] text-5xl lg:text-7xl leading-[0.9]">{name || "SERIES NOT FOUND"}</h1>
        )}
        {name && (
          <Link to={`/rulebook#${slug}`} data-testid="series-read-the-rule" className="mt-3 inline-block link-line text-sm text-[color:var(--vt-body-on-cream)]">
            New here? Read the rule →
          </Link>
        )}
        {name && <p className="mt-3 text-[color:var(--vt-body-on-cream)] text-sm">{articles.length} {articles.length === 1 ? "post" : "posts"}</p>}
      </div>

      {name && articles.length > 8 && (
        <div className="mb-8">
          <label htmlFor="series-search" className="sr-only">Search this series</label>
          <input
            id="series-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search this series…"
            data-testid="series-search"
            className="w-full max-w-md bg-white border-2 border-[color:var(--vt-dark)] px-4 py-3 text-sm text-[color:var(--vt-body-on-cream)] placeholder:text-[#9AA1A6] focus:outline-none focus:border-[color:var(--vt-amber)] transition-colors"
          />
        </div>
      )}

      {loading ? (
        <p className="text-[color:var(--vt-body-on-cream)]">Loading…</p>
      ) : !name ? (
        <p className="text-[color:var(--vt-body-on-cream)]" data-testid="series-notfound">That series doesn't exist. <Link to="/" className="link-line">Back home →</Link></p>
      ) : filtered.length === 0 ? (
        <p className="text-[color:var(--vt-body-on-cream)]" data-testid="series-empty">
          {q ? "No posts match that search." : <>No posts in this series yet. <Link to="/" className="link-line">Back home →</Link></>}
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="series-grid">
          {filtered.map((a, i) => (
            <ArticleCard key={a.slug} article={a} testId={`series-article-${i}`} />
          ))}
        </div>
      )}

      <div className="mt-14 border-t border-[color:var(--vt-dark)]/20 pt-6">
        <Link to="/" className="link-line text-sm">← Back to all content</Link>
      </div>
    </div>
  );
}
